import { useCallback } from "react"
import { AggregationType } from "../constants/charts"
import { TCubeFilterOptions } from "../shared/types/charts"

import useDate from "~/ui-rtk/hooks/date"
import useCurrentCompany from "./current-company"
const dayjs = useDate()

const USE_SESSION_STORAGE = false

let IN_MEMORY_CACHE: Record<string, unknown> = {}

type TCubeChacheKeyOptions = {
  companyId: string
  chartId: string
  dateRange: [Date, Date]
  page: number
  itemsPerPage: number
  granularity?: AggregationType
  cubeFilters?: TCubeFilterOptions
  sorting?: Record<string, unknown>[]
  cubeQueryParams?: Record<string, string>
}

export const getCubeCacheKey = ({
  companyId,
  chartId,
  dateRange,
  page,
  itemsPerPage,
  granularity,
  cubeFilters,
  sorting,
  cubeQueryParams,
}: TCubeChacheKeyOptions) =>
  `${companyId}-${chartId}-${dayjs(dateRange[0]).format("YYYY-MM-DD")}-${dayjs(dateRange[1]).format("YYYY-MM-DD")}${granularity ? `-${granularity}` : ""}-p${page}-ipp${itemsPerPage}-f${JSON.stringify(cubeFilters)}-s${JSON.stringify(sorting)}-d${JSON.stringify(cubeQueryParams)}`

// ToDo: could be `base64` encoded
const METRICS_CACHE_KEY = `MARATHON_DATA_CACHE_KEY_${import.meta.env.VITE_ENV}`

const getParsedStorageItem = (): Record<string, unknown> => {
  const cache = window.sessionStorage.getItem(METRICS_CACHE_KEY)
  if (!cache) {
    return {}
  }
  let parsedCache = {}
  try {
    parsedCache = JSON.parse(cache)
  } catch (e) {
    console.warn("could not parse cache", e)
  }
  return parsedCache ?? {}
}

const getSessionStorageItem = (key: string) => {
  const parsedCache = USE_SESSION_STORAGE
    ? getParsedStorageItem()
    : IN_MEMORY_CACHE
  if (!parsedCache) {
    return null
  }
  return parsedCache[key] ?? null
}

const setSessionStorageItem = (key: string, val: unknown) => {
  if (USE_SESSION_STORAGE) {
    let parsedCache = getParsedStorageItem()
    if (!parsedCache) {
      parsedCache = {} as Record<string, unknown>
    }
    parsedCache[key] = val
    const cache = JSON.stringify(parsedCache)
    window.sessionStorage.setItem(METRICS_CACHE_KEY, cache)
  } else {
    IN_MEMORY_CACHE[key] = val
  }
}

const clearSessionStorageItem = () => {
  window.sessionStorage.removeItem(METRICS_CACHE_KEY)
}

export function clearMetricsCache() {
  IN_MEMORY_CACHE = {}
}

export default function useMetricsCache(
  chartId: string,
  granularity: AggregationType,
  dateRange: [Date, Date],
  onCacheSet: (metrics: unknown) => void,
  page: number,
  itemsPerPage: number,
  cubeFilters?: TCubeFilterOptions,
  sorting?: Record<string, unknown>[],
  cubeQueryParams?: Record<string, string>,
) {
  const currentCompany = useCurrentCompany()
  if (!currentCompany) {
    throw new Error("No current company")
  }

  const { id: companyId } = currentCompany

  const clearCache = clearSessionStorageItem
  const setCacheItem = useCallback(
    (
      chartId: string,
      dateRange: [Date, Date],
      granularity: AggregationType,
      metrics: unknown,
      page: number,
      itemsPerPage: number,
      cubeFilters?: TCubeFilterOptions,
      sorting?: Record<string, unknown>[],
      cubeQueryParams?: Record<string, string>,
    ) => {
      if (!companyId) {
        console.warn("Company id is missing")
        return
      }

      if (!dateRange) {
        return
      }

      const key = getCubeCacheKey({
        companyId,
        chartId,
        dateRange,
        granularity,
        page,
        itemsPerPage,
        cubeFilters,
        sorting,
        cubeQueryParams,
      })
      setSessionStorageItem(key, metrics)
      onCacheSet(metrics)
    },
    [
      chartId,
      dateRange,
      granularity,
      companyId,
      page,
      itemsPerPage,
      cubeFilters,
      sorting,
      cubeQueryParams,
    ],
  )

  if (!dateRange) {
    return {
      isInProgress: () => false,
      data: null,
      clearCache,
      setCacheItem: () => null,
    }
  }

  const key = getCubeCacheKey({
    companyId,
    chartId,
    dateRange,
    granularity,
    page,
    itemsPerPage,
    cubeFilters,
    sorting,
    cubeQueryParams,
  })

  return {
    isInProgress: () => !getSessionStorageItem(key),
    data: getSessionStorageItem(key),
    clearCache,
    setCacheItem,
    page,
  }
}
